import { useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowUp, Loader2, Plus, Sparkles, X } from "lucide-react";
import { api, fmtErr } from "@/lib/api";

const toList = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);
const toStr = (arr) => arr.join(", ");
const has = (arr, v) => arr.some((x) => x.toLowerCase() === v.toLowerCase());

function Chip({ label, onMove, moveUp, onRemove, testid }) {
  const Arrow = moveUp ? ArrowUp : ArrowDown;
  return (
    <span data-testid={testid} className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full text-xs font-medium tracking-wide bg-indigo-50 text-indigo-700 border border-indigo-200">
      {label}
      <button type="button" onClick={onMove} title={moveUp ? "Move to Must-have" : "Move to Nice-to-have"} className="p-0.5 rounded-full hover:bg-indigo-100" data-testid={`${testid}-move`}><Arrow className="w-3 h-3" /></button>
      <button type="button" onClick={onRemove} title="Remove" className="p-0.5 rounded-full hover:bg-indigo-100" data-testid={`${testid}-remove`}><X className="w-3 h-3" /></button>
    </span>
  );
}

function ChipBox({ label, items, moveUp, onMove, onRemove, onAdd, testid }) {
  const [draft, setDraft] = useState("");
  const commit = () => { const v = draft.trim().replace(/,$/, ""); if (v) onAdd(v); setDraft(""); };
  return (
    <div>
      <label className="text-xs text-slate-400 uppercase tracking-wider">{label}</label>
      <div data-testid={testid} className="mt-1 flex flex-wrap gap-1.5 p-2 min-h-[44px] max-h-36 overflow-y-auto rounded-lg bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)] focus-within:border-indigo-500">
        {items.map((s, i) => (
          <Chip key={s} label={s} moveUp={moveUp} testid={`${testid}-chip-${i}`} onMove={() => onMove(s)} onRemove={() => onRemove(s)} />
        ))}
        <input
          data-testid={`${testid}-input`} value={draft} placeholder={items.length ? "Add more" : "Type a skill and press Enter"}
          onChange={(e) => (e.target.value.endsWith(",") ? (onAdd(e.target.value.slice(0, -1).trim()), setDraft("")) : setDraft(e.target.value))}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); commit(); }
            if (e.key === "Backspace" && !draft && items.length) onRemove(items[items.length - 1]);
          }}
          onBlur={commit}
          className="flex-1 min-w-[120px] bg-transparent outline-none text-sm px-1 py-0.5"
        />
      </div>
    </div>
  );
}

// Must/Nice skill chips, auto-extracted by AI from the job description (debounced).
export default function JobSkillsFields({ title, description, must, nice, onChange, prefix = "job", autoExtract = true }) {
  const mustL = toList(must), niceL = toList(nice);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const lastDesc = useRef(description || "");
  const reqId = useRef(0);

  const set = (m, n) => onChange({ must_haves: toStr(m), nice_to_haves: toStr(n) });

  const extract = async (desc) => {
    const id = ++reqId.current;
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/jobs/extract-skills", { title, description: desc });
      if (id !== reqId.current) return;
      set(data.must_haves, data.nice_to_haves);
      setSuggestions(data.suggestions || []);
    } catch (e) {
      if (id === reqId.current) setErr(fmtErr(e.response?.data?.detail) || "Couldn't extract skills");
    } finally { if (id === reqId.current) setBusy(false); }
  };

  useEffect(() => {
    const d = (description || "").trim();
    if (!autoExtract || d === lastDesc.current.trim() || d.length < 60) return;
    const t = setTimeout(() => { lastDesc.current = d; extract(d); }, 1500);
    return () => clearTimeout(t);
  }, [description]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = (list, v) => (has(list, v) ? list : [...list, v]);
  const without = (list, v) => list.filter((x) => x.toLowerCase() !== v.toLowerCase());
  const pending = suggestions.filter((s) => !has(mustL, s) && !has(niceL, s));
  const canExtract = (description || "").trim().length >= 60;

  return (
    <div className="grid gap-3" data-testid={`${prefix}-skills`}>
      <div className="flex items-center justify-between gap-2 min-h-[20px] text-xs">
        <span data-testid={`${prefix}-skills-status`} className={err ? "text-rose-600" : "text-slate-500"}>
          {busy ? <span className="inline-flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Reading the job description for skills…</span>
            : err || (canExtract ? "Skills are extracted from the description automatically." : "Paste the job description to auto-extract skills.")}
        </span>
        {canExtract && !busy && (
          <button type="button" data-testid={`${prefix}-extract-skills`} onClick={() => { lastDesc.current = description.trim(); extract(description.trim()); }}
            className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-500 font-medium shrink-0">
            <Sparkles className="w-3.5 h-3.5" /> {mustL.length || niceL.length ? "Re-extract" : "Extract skills from description"}
          </button>
        )}
      </div>
      <ChipBox label="Must-have skills" items={mustL} testid={`${prefix}-must`}
        onMove={(s) => set(without(mustL, s), add(niceL, s))} onRemove={(s) => set(without(mustL, s), niceL)}
        onAdd={(v) => v && set(add(mustL, v), without(niceL, v))} />
      <ChipBox label="Nice-to-have skills" items={niceL} moveUp testid={`${prefix}-nice`}
        onMove={(s) => set(add(mustL, s), without(niceL, s))} onRemove={(s) => set(mustL, without(niceL, s))}
        onAdd={(v) => v && set(without(mustL, v), add(niceL, v))} />
      {pending.length > 0 && (
        <div data-testid={`${prefix}-skill-suggestions`}>
          <div className="text-xs text-slate-400 uppercase tracking-wider mb-1">More skills found — click to add</div>
          <div className="flex flex-wrap gap-1.5">
            {pending.map((s, i) => (
              <span key={s} className="inline-flex items-center rounded-full border border-dashed border-slate-300 text-xs text-slate-600 overflow-hidden">
                <button type="button" data-testid={`${prefix}-suggest-must-${i}`} onClick={() => set(add(mustL, s), niceL)} className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 hover:bg-indigo-50 hover:text-indigo-700" title="Add as Must-have">
                  <Plus className="w-3 h-3" /> {s}
                </button>
                <button type="button" data-testid={`${prefix}-suggest-nice-${i}`} onClick={() => set(mustL, add(niceL, s))} className="px-2 py-1 border-l border-dashed border-slate-300 hover:bg-indigo-50 hover:text-indigo-700" title="Add as Nice-to-have">nice</button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
